import {
  businessDate,
  daysBetween,
  DEFAULT_DISPLAY_ZONE,
  businessDateInZone,
  instantFromISOString,
  systemClock,
  zonedPartsOf,
} from '@sakhtar/calendar';
import { faDigits } from '../../lib/faDigits.js';
import type { AmountDisplayUnit } from '../../lib/finance-display.js';

/**
 * «۱٫۴۸ میلیارد» / «۳۱۲ میلیون» — a compact display of an integer-Rial
 * string, computed with BigInt (display only; never fed back into money).
 */
export function compactAmount(
  rialDigits: string,
  unit: AmountDisplayUnit,
): { readonly value: string; readonly scale: string } {
  let amount = BigInt(rialDigits || '0');
  if (unit === 'toman') amount = amount / 10n;
  const negative = amount < 0n;
  const abs = negative ? -amount : amount;
  const sign = negative ? '-' : '';
  const unitLabel = unit === 'toman' ? 'تومان' : 'ریال';
  const scaled = (divisor: bigint, decimals: number) => {
    const factor = 10n ** BigInt(decimals);
    const rounded = (abs * factor + divisor / 2n) / divisor;
    const whole = rounded / factor;
    const fraction = (rounded % factor).toString().padStart(decimals, '0').replace(/0+$/, '');
    const text = fraction ? `${whole}٫${fraction}` : `${whole}`;
    return faDigits(`${sign}${text}`);
  };
  if (abs >= 1_000_000_000n)
    return { value: scaled(1_000_000_000n, 2), scale: `میلیارد ${unitLabel}` };
  if (abs >= 1_000_000n)
    return { value: scaled(1_000_000n, abs >= 100_000_000n ? 0 : 1), scale: `میلیون ${unitLabel}` };
  return {
    value: faDigits(`${sign}${abs.toLocaleString('en-US').replace(/,/g, '٬')}`),
    scale: unitLabel,
  };
}

/** Percent change with one decimal, or null when there is no base to compare. */
export function percentChange(current: string, previous: string): number | null {
  const cur = BigInt(current || '0');
  const prev = BigInt(previous || '0');
  if (prev <= 0n) return null;
  const tenths = ((cur - prev) * 1000n) / prev;
  return Number(tenths) / 10;
}

/** Whole percent `part / whole`, clamped 0–100. */
export function sharePercent(part: string, whole: string): number {
  const w = BigInt(whole || '0');
  if (w <= 0n) return 0;
  const pct = (BigInt(part || '0') * 100n) / w;
  return Math.max(0, Math.min(100, Number(pct)));
}

export function greeting(): string {
  const hour = zonedPartsOf(systemClock.now(), DEFAULT_DISPLAY_ZONE).hour;
  if (hour >= 5 && hour < 12) return 'صبح بخیر';
  if (hour >= 12 && hour < 16) return 'ظهر بخیر';
  if (hour >= 16 && hour < 20) return 'عصر بخیر';
  return 'شب بخیر';
}

export type DueTone = 'late' | 'today' | 'later';

/** «سررسید: دیروز» / «امروز» / «۳ روز دیگر» for a date or an ISO instant. */
export function dueLabel(
  due: string | null,
  kind: 'date' | 'instant' | null,
): { readonly label: string; readonly tone: DueTone } | null {
  if (!due || !kind) return null;
  try {
    const today = businessDateInZone(systemClock.now(), DEFAULT_DISPLAY_ZONE);
    const day =
      kind === 'date'
        ? businessDate(due)
        : businessDateInZone(instantFromISOString(due), DEFAULT_DISPLAY_ZONE);
    const diff = daysBetween(today, day);
    if (diff < 0) {
      return {
        label: diff === -1 ? 'سررسید: دیروز' : `سررسید: ${faDigits(-diff)} روز پیش`,
        tone: 'late',
      };
    }
    if (diff === 0) {
      if (kind === 'instant') {
        const parts = zonedPartsOf(instantFromISOString(due), DEFAULT_DISPLAY_ZONE);
        const time = faDigits(
          `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`,
        );
        return { label: `امروز · ${time}`, tone: 'today' };
      }
      return { label: 'امروز', tone: 'today' };
    }
    if (diff === 1) return { label: 'فردا', tone: 'later' };
    if (diff <= 7) return { label: `${faDigits(diff)} روز دیگر`, tone: 'later' };
    return { label: diff <= 14 ? 'هفتهٔ آینده' : `${faDigits(diff)} روز دیگر`, tone: 'later' };
  } catch {
    return null;
  }
}

/** «م.ا» from «مریم اکبری». */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '؟';
  if (parts.length === 1) return parts[0]!.slice(0, 1);
  return `${parts[0]!.slice(0, 1)}.${parts[parts.length - 1]!.slice(0, 1)}`;
}

const ENTITY_LABEL: Record<string, string> = {
  'sales.quote': 'پیش‌فاکتور',
  'sales.order': 'سفارش فروش',
  'sales.invoice': 'فاکتور فروش',
  'sales.commission': 'پورسانت',
  'sales.commission-rule': 'پورسانت محصول',
  'crm.lead': 'سرنخ',
  'crm.opportunity': 'فرصت فروش',
  'crm.activity': 'فعالیت',
  'crm.note': 'یادداشت',
  'accounting.journal-entry': 'سند حسابداری',
  'accounting.journal': 'سند حسابداری',
  'accounting.ar-open-item': 'دریافتنی',
  'accounting.ap-open-item': 'پرداختنی',
  'accounting.treasury-transaction': 'دریافت/پرداخت',
  'accounting.fiscal-period': 'دورهٔ مالی',
  'inventory.receipt': 'رسید انبار',
  'inventory.issue': 'حوالهٔ انبار',
  'inventory.transfer': 'انتقال انبار',
  'inventory.movement': 'گردش انبار',
  'inventory.reservation': 'رزرو کالا',
  'procurement.request': 'درخواست خرید',
  'procurement.order': 'سفارش خرید',
  'procurement.receipt': 'رسید خرید',
  'projects.project': 'پروژه',
  'projects.task': 'کار',
  'projects.todo': 'کار شخصی',
  'projects.reminder': 'یادآوری',
  'master.party': 'طرف‌حساب',
  'master.product': 'کالا',
  'master.warehouse': 'انبار',
};

const VERB_LABEL: Record<string, string> = {
  create: 'ثبت شد',
  update: 'ویرایش شد',
  submit: 'ارسال شد',
  approve: 'تأیید شد',
  confirm: 'تأیید شد',
  post: 'ثبت قطعی شد',
  reverse: 'برگشت خورد',
  'reverse-request': 'درخواست برگشت شد',
  cancel: 'لغو شد',
  accept: 'پذیرفته شد',
  reject: 'رد شد',
  complete: 'انجام شد',
  deliver: 'تحویل شد',
  delete: 'حذف شد',
  import: 'از فایل وارد شد',
  assign: 'واگذار شد',
  close: 'بسته شد',
  reopen: 'باز شد',
  accrue: 'محاسبه شد',
  settle: 'تسویه شد',
};

/** Persian sentence for an audit action, e.g. `sales.order.confirm` → «سفارش فروش تأیید شد». */
export function describeActivity(action: string): {
  readonly subject: string;
  readonly verb: string;
} {
  const [module = '', entity = '', verb = ''] = action.split('.');
  const subject = ENTITY_LABEL[`${module}.${entity}`] ?? 'رکورد';
  return { subject, verb: VERB_LABEL[verb] ?? 'به‌روز شد' };
}

export const MODULE_LABEL: Record<string, string> = {
  finance: 'مالی',
  accounting: 'مالی',
  procurement: 'خرید',
  crm: 'فروش',
  sales: 'فروش',
  inventory: 'انبار',
  projects: 'پروژه',
  master: 'اطلاعات پایه',
};

/** design-system `[data-module]` value for a dashboard/audit module key. */
export function moduleTint(module: string): string | undefined {
  if (module === 'accounting' || module === 'finance') return 'finance';
  if (module === 'sales' || module === 'crm') return 'crm';
  if (module === 'inventory' || module === 'procurement' || module === 'projects') return module;
  return undefined;
}
